import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
    useForm,
} from 'react-hook-form-mui';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';

import {
    BusinessInput,
    PourAvailabilityInput,
} from '@barboxx/admin-components';
import {
    CREATE_BUSINESS_POUR_AVAILABILITY_SCHEMA,
    useBusinessSelector,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { BusinessPourAvailability } from '@barboxx/models';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreatePourAvailabilityDialog = ({
    onClose,
    onSuccess,
    open,
}: Props) => {
    const { businessId } = useBusinessSelector();
    const notifications = useNotifications();

    const formContext = useForm<BusinessPourAvailability.CreateRequest>({
        resolver: yupResolver(CREATE_BUSINESS_POUR_AVAILABILITY_SCHEMA),
        defaultValues: {
            isDefault: false,
            notAllowed: false,
            isActive: true,
            upCharge: 1,
            basePrice: undefined,
            businessId,
        },
    });

    const mutateCreatePourAvailability = useMutation({
        mutationKey: ['pourAvailability'],
        mutationFn: (data: BusinessPourAvailability.CreateRequest) =>
            Api.BusinessSpecific.PourAvailability.postPourAvailability(data),
    });

    const onSubmit = async (values: BusinessPourAvailability.CreateRequest) => {
        return mutateCreatePourAvailability
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created Pour availability!`, {
                    severity: 'success',
                });
                handleCancelCreate();
                onSuccess();
            })
            .catch(e => {
                notifications.show('Error creating pour availability!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancelCreate}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">
                Create Pour Availability
            </DialogTitle>
            <FormContainer onSuccess={onSubmit} formContext={formContext}>
                <DialogContent
                    sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}
                >
                    <BusinessInput />
                    <PourAvailabilityInput />

                    <TextFieldElement
                        label="basePrice"
                        name="basePrice"
                        fullWidth
                    />
                    <TextFieldElement
                        label="upCharge"
                        name="upCharge"
                        fullWidth
                    />
                    <TextFieldElement
                        label="displayOrder"
                        name="displayOrder"
                        fullWidth
                    />

                    <CheckboxElement label="isActive" name="isActive" />
                    <CheckboxElement label="Is Default" name="isDefault" />
                    <CheckboxElement label="Not Allowed" name="notAllowed" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelCreate} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        loading={formContext.formState.isSubmitting}
                        type="submit"
                        disabled={formContext.formState.isSubmitting}
                    >
                        Create
                    </Button>
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
};
